/**
 * Service responsible for managing user session tracking across the checkout flow
 * Generates a unique session ID when user enters and persists it throughout the journey
 */

// Função para gerar UUID v4 usando crypto nativo do browser
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const SESSION_ID_KEY = 'checkout_session_id';
const SESSION_START_KEY = 'checkout_session_start';
const SESSION_METADATA_KEY = 'checkout_session_metadata';

export interface SessionMetadata {
  userEmail?: string;
  userName?: string;
  currentStep?: string;
  startedAt: string;
  lastActivityAt: string;
  eventsCount: number;
}

class SessionService {
  private sessionId: string;
  private metadata: SessionMetadata;

  constructor() {
    this.sessionId = this.initializeSessionId();
    this.metadata = this.loadMetadata();
    this.updateLastActivity();
  }

  /**
   * Initializes or retrieves existing session ID
   */
  private initializeSessionId(): string {
    let sessionId = localStorage.getItem(SESSION_ID_KEY);

    if (!sessionId) {
      sessionId = `session_${generateUUID()}`;
      localStorage.setItem(SESSION_ID_KEY, sessionId);
      localStorage.setItem(SESSION_START_KEY, new Date().toISOString());
    }

    return sessionId;
  }

  /**
   * Loads session metadata from localStorage
   */
  private loadMetadata(): SessionMetadata {
    const stored = localStorage.getItem(SESSION_METADATA_KEY);
    const startTime = localStorage.getItem(SESSION_START_KEY) || new Date().toISOString();

    if (stored) {
      return JSON.parse(stored);
    }

    return {
      startedAt: startTime,
      lastActivityAt: new Date().toISOString(),
      eventsCount: 0,
    };
  }

  /**
   * Saves metadata to localStorage
   */
  private saveMetadata(): void {
    localStorage.setItem(SESSION_METADATA_KEY, JSON.stringify(this.metadata));
  }

  /**
   * Gets the current session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Gets session metadata
   */
  getMetadata(): SessionMetadata {
    return { ...this.metadata };
  }

  /**
   * Updates user information in session
   */
  updateUserInfo(userName?: string, userEmail?: string): void {
    if (userName) this.metadata.userName = userName;
    if (userEmail) this.metadata.userEmail = userEmail;
    this.updateLastActivity();
    this.saveMetadata();
  }

  /**
   * Updates current step in checkout flow
   */
  updateCurrentStep(step: string): void {
    this.metadata.currentStep = step;
    this.updateLastActivity();
    this.saveMetadata();
  }

  /**
   * Increments event counter
   */
  incrementEventCount(): void {
    this.metadata.eventsCount++;
    this.updateLastActivity();
    this.saveMetadata();
  }

  /**
   * Updates last activity timestamp
   */
  private updateLastActivity(): void {
    this.metadata.lastActivityAt = new Date().toISOString();
  }

  /**
   * Clears session data (used when checkout is completed)
   */
  clearSession(): void {
    localStorage.removeItem(SESSION_ID_KEY);
    localStorage.removeItem(SESSION_START_KEY);
    localStorage.removeItem(SESSION_METADATA_KEY);
    this.sessionId = this.initializeSessionId();
    this.metadata = this.loadMetadata();
  }

  /**
   * Gets session duration in seconds
   */
  getSessionDuration(): number {
    const start = new Date(this.metadata.startedAt);
    const now = new Date();
    return Math.floor((now.getTime() - start.getTime()) / 1000);
  }
}

// Export singleton instance
export const sessionService = new SessionService();
